import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import PageLoadingShell from "../layout/PageLoadingShell";
import ListPageSkeleton from "../layout/ListPageSkeleton";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { Routes as AppRoutes } from "../../constants/RouteConstants";
import { Config } from "../../config/Config";
import {
  saveStripeCheckoutSessionId,
} from "../../utils/stripeCheckout";
import {
  CurrencyConvertHistory,
  CurrencyIcon,
  formatDisplayLongDate,
  getDate,
  getFromStorage,
  getPriceHelper,
} from "../../utils/Helper";
import {
  formatCheckoutAddressLine,
  getCheckoutItemCount,
  getCheckoutResidenceLabel,
  getQuotationTaxAmount,
} from "../../utils/quotationCheckout";
import { socket } from "../../utils/Socket";
import truckIcon from "../../Assets/images/Truck.svg";
import OrderSuccessOverlay from "../layout/OrderSuccessOverlay";
import {
  parseChargeLineAmount,
  parseOtherChargesLines,
} from "./view-quotation/helpers";
import "./QuotationCheckout.scss";

function QuotationCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();

  const quotationView = useMakeRequest();
  const OrderAdd = useMakeRequest();

  const [currency, setCurrency] = useState<"USD" | "EUR">("USD");
  const [payMethod, setPayMethod] = useState("");
  const [error, setError] = useState("");
  const [otherChargesModalOpen, setOtherChargesModalOpen] = useState(false);
  const [wireSuccessOpen, setWireSuccessOpen] = useState(false);

  const sawQuotViewLoadingRef = useRef(false);
  const redirectedRef = useRef(false);
  const wireSuccessShownRef = useRef(false);

  useEffect(() => {
    sawQuotViewLoadingRef.current = false;
    if (!id) return;
    quotationView.makeRequest({
      payload: { quotation_id: id },
      url: ApiConstants?.QuotesView,
      toastStatus: false,
    });
  }, [id]);

  useEffect(() => {
    if (quotationView.loading) {
      sawQuotViewLoadingRef.current = true;
    }
  }, [quotationView.loading]);

  const quotationData = quotationView?.data?.data;
  const quotationReady = Boolean(quotationView?.status && quotationData);
  const quotationFetchSettled =
    Boolean(id) && !quotationView.loading && sawQuotViewLoadingRef.current;
  const showLoader = Boolean(id) && !quotationReady && !quotationFetchSettled;
  const showCheckoutPageLoader = showLoader || Boolean(OrderAdd?.loading);

  useEffect(() => {
    if (!quotationReady || redirectedRef.current) return;

    const anotherProduct = getFromStorage({ key: "another_product" });
    if (anotherProduct != null && String(anotherProduct?.quotation_id) === String(id)) {
      redirectedRef.current = true;
      toast.error(LanguageConstants?.MESSAGE_FOR_PRODUCT);
      navigate(AppRoutes.Viewquote.replace(":id", String(id)));
      return;
    }

    if (String(quotationData?.status) !== "1") {
      redirectedRef.current = true;
      toast.error("This quotation is not ready for checkout.");
      navigate(AppRoutes.Viewquote.replace(":id", String(id)));
    }
  }, [quotationReady, quotationData?.status, id, navigate]);

  const numBk = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatMoney = (value: unknown) => {
    const converted = CurrencyConvertHistory(numBk(value), currency);
    return `${CurrencyIcon(currency)}${getPriceHelper(converted)}`;
  };

  const taxAmount = useMemo(
    () => getQuotationTaxAmount(quotationData),
    [quotationData],
  );

  const itemCount = useMemo(
    () => getCheckoutItemCount(quotationData),
    [quotationData],
  );

  const otherChargesLines = useMemo(
    () => parseOtherChargesLines(quotationData?.charges_reason),
    [quotationData?.charges_reason],
  );
  const otherChargesTotal = numBk(quotationData?.other_charges);
  const hasOtherChargesDetail =
    otherChargesLines.length > 0 || otherChargesTotal > 0;

  const longDate = formatDisplayLongDate(
    quotationData?.created_at,
    getDate(quotationData?.created_at),
  );

  const addr = quotationData?.address_detail;
  const instructionsText =
    addr?.instructions != null && String(addr.instructions).trim() !== ""
      ? String(addr.instructions).trim()
      : "—";

  const showWireTransfer =
    quotationData?.total_amount != null &&
    Number(quotationData.total_amount) >= 20000;

  useEffect(() => {
    if (quotationReady && !showWireTransfer && !payMethod) {
      setPayMethod("0");
    }
  }, [quotationReady, showWireTransfer, payMethod]);

  const handlePayment = () => {
    if (OrderAdd.loading) return;
    if (!payMethod) {
      setError(LanguageConstants?.SELETE_PAYMENT_ERROR ?? "Please select payment method");
      return;
    }
    if (!quotationData) return;

    const payload = {
      quotation_id: quotationData?.quotation_id,
      address_id: quotationData?.address_id,
      total_amount: quotationData?.total_amount,
      payment_type: payMethod,
      success_url: Config.REACT_APP_SUCCESSFULLY,
      cancel_url: Config.REACT_APP_FAILED,
      type: "web",
    };
    OrderAdd.makeRequest({ payload, url: ApiConstants?.OrderAdd, toastStatus: false });
    setError("");
  };

  useEffect(() => {
    if (!OrderAdd?.status) return;
    if (OrderAdd.data?.data?.url) {
      const sessionId = OrderAdd.data?.data?.id;
      if (sessionId) saveStripeCheckoutSessionId(String(sessionId));
      window.location.href = OrderAdd.data.data.url;
      return;
    }
    if (wireSuccessShownRef.current) return;
    wireSuccessShownRef.current = true;
    socket.emit("admin_count", { sender_type: "1", receiver_id: "1" }, async () => {});
    setWireSuccessOpen(true);
  }, [OrderAdd?.status]);

  const viewQuotePath = id
    ? AppRoutes.Viewquote.replace(":id", String(id))
    : AppRoutes.MyQuote;

  const renderPlaceOrderButton = (className?: string) => (
    <div className="row mt-5">
      <div className="col-11 col-sm-7 mx-auto">
        <button
          type="button"
          className={`round-btn w-100${className ? ` ${className}` : ""}`}
          onClick={handlePayment}
          disabled={OrderAdd?.loading || !quotationReady}
          aria-busy={OrderAdd?.loading}
        >
          {LanguageConstants?.PLACE_ORDER ?? "Proceed with Payment"}
        </button>
      </div>
    </div>
  );

  if (!id) {
    return (
      <div className="head-space">
        <div className="inner-content">
          <div className="quote-checkout-page">
            <div className="qc-page__shell">
              <p className="qc-page__empty">{LanguageConstants?.DATANOTFOUND ?? "Not found"}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLoadingShell
        loading={showCheckoutPageLoader}
        skeleton={<ListPageSkeleton />}
        className="head-space"
      >
        <div className="inner-content">
          <div className="quote-checkout-page">
            <div className="container">
              <header className="qc-page__header">
                <button
                  type="button"
                  className="qc-page__navBack"
                  onClick={() => navigate(viewQuotePath)}
                  aria-label={`${LanguageConstants?.BACK ?? "Back"} — ${LanguageConstants?.VIEW_QUOTE_DETAILS ?? "Quote Details"}`}
                >
                  <i className="icon-chev-right qc-page__navBackIcon" aria-hidden />
                  {LanguageConstants?.CHECKOUT ?? "Checkout"}
                </button>

                {quotationReady ? (
                  <div className="qc-page__quoteMeta">
                    <p className="qc-page__metaLine qc-page__metaLine--primary">
                      {LanguageConstants?.VIEW_QUOTE_DETAILS ?? "Quote Details"}:{" "}
                      <span className="qc-page__metaValue">{quotationData?.quotation_id}</span>
                    </p>
                    <p className="qc-page__metaLine">
                      {LanguageConstants?.VIEW_QUOTE_DATE_LABEL ?? "Date"}:{" "}
                      <span className="qc-page__metaValue">{longDate}</span>
                    </p>
                    <p className="qc-page__metaLine">
                      {LanguageConstants?.VIEW_QUOTE_ITEMS_COUNT ?? "Items"}{" "}
                      <span className="qc-page__metaValue">{itemCount}</span>
                    </p>
                  </div>
                ) : null}
              </header>

              {quotationReady ? (
                <>
                  <div className="qc-page__columns">
                    <section className="qc-card qc-card--pricing" aria-labelledby="qc-breakdown-title">
                      <div className="qc-breakdown__head">
                        <h2 id="qc-breakdown-title" className="qc-card__sectionTitle">
                          {LanguageConstants?.PRICE_BRACKDOWN ?? "Price Breakdown"}
                        </h2>
                        <label className="qc-breakdown__toggle">
                          <span className="qc-breakdown__switch">
                            <input
                              type="checkbox"
                              checked={currency === "EUR"}
                              onChange={(e) => setCurrency(e.target.checked ? "EUR" : "USD")}
                              aria-label={LanguageConstants?.CONVERTINPISO}
                            />
                            <span className="qc-breakdown__switchTrack" aria-hidden />
                          </span>
                          <span className="qc-breakdown__toggleText">
                            {LanguageConstants?.CONVERTINPISO ?? "Convert prices in euro"}
                          </span>
                        </label>
                      </div>

                      <div className="qc-breakdown__rows">
                        <div className="qc-breakdown__row">
                          <span>
                            {LanguageConstants?.TOTAL_PRODUCT_PRICE ??
                              LanguageConstants?.TOTALPRODUCTAMOUNT}
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotationData?.total_product_price)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span>
                            {LanguageConstants?.SHIPPINGPRICE ??
                              LanguageConstants?.TOTALSHIIPNGAMOUNT}
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotationData?.total_shipping_price)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span className="qc-breakdown__rowLabel">
                            {LanguageConstants?.OTHER_CARGERS}
                            <button
                              type="button"
                              className={`qc-breakdown__info${
                                hasOtherChargesDetail
                                  ? ""
                                  : " qc-breakdown__info--disabled"
                              }`}
                              disabled={!hasOtherChargesDetail}
                              aria-disabled={!hasOtherChargesDetail}
                              title={
                                hasOtherChargesDetail
                                  ? LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_BTN ??
                                    "View other charges breakdown"
                                  : LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_NONE ??
                                    "No other charges details"
                              }
                              aria-label={
                                LanguageConstants?.Charges_reason ??
                                "Other charges details"
                              }
                              onClick={() => {
                                if (hasOtherChargesDetail) {
                                  setOtherChargesModalOpen(true);
                                }
                              }}
                            >
                              <i className="icon-info" aria-hidden />
                            </button>
                          </span>
                          <span className="qc-breakdown__rowVal">
                            {formatMoney(quotationData?.other_charges ?? 0)}
                          </span>
                        </div>
                        <div className="qc-breakdown__row">
                          <span>{LanguageConstants?.TOTALTAX}</span>
                          <span className="qc-breakdown__rowVal">{formatMoney(taxAmount)}</span>
                        </div>
                      </div>

                      <div className="qc-breakdown__divider" />

                      <div className="qc-breakdown__grand">
                        <span className="qc-breakdown__grandLabel">
                          {LanguageConstants?.GRANDTOTAL ?? "Grand Total"}
                        </span>
                        <div className="qc-breakdown__grandRight">
                          <p className="qc-breakdown__usdNote">
                            {currency === "EUR"
                              ? LanguageConstants?.VIEW_QUOTE_FOLLOW_EUR ??
                                "Following price is in EUR"
                              : LanguageConstants?.PRIZEMESSAGE}
                          </p>
                          <span className="qc-breakdown__grandAmt">
                            {formatMoney(quotationData?.total_amount)}
                          </span>
                        </div>
                      </div>

                      <div className="qc-paymentBlock">
                        <h3 className="qc-paymentBlock__title">
                          {LanguageConstants?.PAYMENT_MATHOD ?? "Payment Method"}
                        </h3>
                        <div className="qc-payment">
                          <label className="qc-payment__option">
                            <input
                              className="qc-payment__radio"
                              type="radio"
                              name="paymentMethod"
                              checked={payMethod === "0"}
                              onChange={() => setPayMethod("0")}
                            />
                            <span className="qc-payment__label">
                              {LanguageConstants?.ONLINE ?? "Online"}
                            </span>
                          </label>

                          {showWireTransfer ? (
                            <label className="qc-payment__option">
                              <input
                                className="qc-payment__radio"
                                type="radio"
                                name="paymentMethod"
                                checked={payMethod === "1"}
                                onChange={() => setPayMethod("1")}
                              />
                              <span className="qc-payment__label">
                                {LanguageConstants?.OFFLIE ?? "Bank wire transfer"}
                              </span>
                            </label>
                          ) : null}
                        </div>
                        {error ? <p className="qc-page__error">{error}</p> : null}
                      </div>

                      {renderPlaceOrderButton()}
                    </section>

                    <section className="qc-card qc-card--shipping" aria-labelledby="qc-shipping-title">
                      <div className="qc-shipping__head" id="qc-shipping-title">
                        <img src={truckIcon} alt="" className="qc-shipping__headIcon" />
                        <span className="qc-shipping__headText">
                          {LanguageConstants?.MANUAL_QUOTE_SHIPPING_DETAILS ?? "Shipping Details"}
                        </span>
                      </div>

                      <div className="qc-shipping__body">
                        <div className="qc-shipping__rows">
                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION ?? "Delivery Address"}
                            </div>
                            <p className="qc-shipping__value">
                              {formatCheckoutAddressLine(quotationData)}
                            </p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.INSTRUCTIONS ?? "Instructions"}
                            </div>
                            <p className="qc-shipping__value">{instructionsText}</p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_RESIDENCE ?? "Type of Residence"}
                            </div>
                            <p className="qc-shipping__value">
                              {getCheckoutResidenceLabel(addr?.loaction_type)}
                            </p>
                          </div>

                          <div className="qc-shipping__row">
                            <div className="qc-shipping__label">
                              {LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION ??
                                "Shipping Method"}
                            </div>
                            <p className="qc-shipping__value">
                              {quotationData?.shipping_Trans_detail?.name ?? "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </>
              ) : quotationFetchSettled ? (
                <p className="qc-page__empty">{LanguageConstants?.DATANOTFOUND ?? "Not found"}</p>
              ) : null}
            </div>
          </div>
        </div>
      </PageLoadingShell>

      <OrderSuccessOverlay show={wireSuccessOpen} isWireTransfer />

      <Modal
        show={otherChargesModalOpen}
        onHide={() => setOtherChargesModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_TITLE ??
              "Other Charges Breakdown"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="qc-otherChargesModal">
            {otherChargesLines.length > 0 ? (
              <ul className="qc-otherChargesModal__list">
                {otherChargesLines.map((line, idx) => (
                  <li
                    key={`${line.label}-${idx}`}
                    className="qc-otherChargesModal__row"
                  >
                    <span className="qc-otherChargesModal__label">
                      {line.label}
                    </span>
                    <span className="qc-otherChargesModal__amt">
                      {CurrencyIcon(currency)}
                      {getPriceHelper(
                        CurrencyConvertHistory(
                          parseChargeLineAmount(line.amount),
                          currency,
                        ),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="qc-otherChargesModal__empty">
                {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_EMPTY ??
                  "No itemized charges recorded."}
              </p>
            )}
            <div className="qc-otherChargesModal__total">
              <span>{LanguageConstants?.OTHER_CARGERS}</span>
              <span className="qc-otherChargesModal__totalAmt">
                {CurrencyIcon(currency)}
                {getPriceHelper(
                  CurrencyConvertHistory(otherChargesTotal, currency),
                )}
              </span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default QuotationCheckout;
